import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import TextInfo from "@/bit/components/components.text";
import NoticeBlock from "@/bit/components/components.cms-block.cms-notice-block";
import dynamic from "next/dynamic";
import getUrlParams from "@gcloud-npm/utils.common.get-url-params";
import RuleEvaluator from "@gcloud-npm/utils.rule-evaluator";
import SecureLocalStorage from "@gcloud-npm/utils.secure.local-storage";

const CmsAddElement = dynamic(() =>
  import("@/bit/components/components.cms-block.cms-add-element")
);

const ID_TEMPLATE_ORION = "1836062";

const checkUserPQ = (ts, rules) => {
  let res = false;
  let ruleEvaluator = null;
  try {
    if (Object.keys(rules).length > 0) {
      ruleEvaluator = new RuleEvaluator(rules);
      res = ruleEvaluator.test(ts);
    }
  } catch (error) {
    console.log("Error checkUserPQ: ", error);
  }
  return res;
};

function isEmptyObject(obj) {
  return (
    obj &&
    typeof obj === "object" &&
    !Array.isArray(obj) &&
    Object.keys(obj).length === 0
  );
}

const filterListByJsonRules = (list, ts) => {
  return list.filter((item) => {
    const jsonRules = item["cms-hiring-details-1"]?.["cms-json-rules-1"];
    const isEmptyOrNull = !jsonRules || isEmptyObject(jsonRules);

    return isEmptyOrNull || checkUserPQ(ts, jsonRules);
  });
};

const checkIfEmailNeedsChange = (ts) => {
  let res = false;
  if (ts) {
    try {
      res = JSON.stringify(ts).includes("colAct0069");
    } catch (e) {}
  }
  return res;
};

const changeClientEmail = (content, email) => {
  let res = content;
  try {
    let contentString = JSON.stringify(content);
    if (email && Array.isArray(email) && email.length > 0) {
      const clientEmail = email[0].email;
      if (contentString.includes("nombre.apellidos@gmail.com")) {
        contentString = contentString.replace(
          " en nombre.apellidos@gmail.com",
          ` en ${clientEmail}`
        );
      } else {
        // Si ya lo he reemplazado antes
        contentString = contentString.replace(
          "un correo con las instrucciones",
          `un correo en ${clientEmail} con las instrucciones`
        );
      }
    } else {
      contentString = contentString.replace(
        " en nombre.apellidos@gmail.com",
        ""
      );
    }
    res = JSON.parse(contentString);
  } catch (e) {
    console.log("No se pudo reemplazar el email: ", e);
  }
  return res;
};

const setInitialNoticeList = (list) => {
  let res = [];
  if (list && Array.isArray(list) && list.length > 0) {
    try {
      res = list.slice(-3);
    } catch (e) {
      console.log("No hay 3 elementos en la lista: ", e);
      res = [list[0]];
    }
  }
  return res;
};

const NoticeBlockList = (props) => {
  const {
    "cms-atom-text-1": title,
    "default-notice-title-1": defaultTitle,
    "notice-block-list-1": noticeBlockList,
    "default-notice-block-1": defaultNotice,
    cmsHandler,
    blockName,
    alias,
  } = props;
  const [filteredList, setFilteredList] = useState(
    setInitialNoticeList(noticeBlockList)
  );
  const [flags, setFlags] = useState("");
  const [defaultView, setDefaultView] = useState(false);
  const [loadedContent, setLoadedContent] = useState(false);
  const emailList = useSelector((state) => state.client?.emailMovistarPlus);

  if (!noticeBlockList || !Array.isArray(noticeBlockList)) {
    return null;
  }

  useEffect(() => {
    // Extraemos secure y concatenamos con parametros
    const secureStorage = new SecureLocalStorage();
    let ts = secureStorage.getItem("ts");

    if (!loadedContent) {
      // Extraemos parametros de url
      const urlParams = getUrlParams({
        groupByObject: false,
        allowedParams: false,
        excludeHash: true,
        currentUrl: window.location.href,
      });

      // Concatenamos secure con parametros
      ts = { ...ts, urlParams };
      let flags = ts?.userPo?.FLAGS || "";

      // Actualizamos lista con filtros de json rules
      const appliedJsonRules = filterListByJsonRules(noticeBlockList, ts);
      if (
        appliedJsonRules &&
        Array.isArray(appliedJsonRules) &&
        appliedJsonRules.length > 0
      ) {
        // Miro si cliente cumple card de cambiar email
        const changeEmailIfNeeded = checkIfEmailNeedsChange(ts)
          ? changeClientEmail(appliedJsonRules, emailList)
          : appliedJsonRules;

        setFilteredList(changeEmailIfNeeded);
      } else {
        // Si no se cumple ninguna muestro por defecto
        setFilteredList([defaultNotice]);
        setDefaultView(true);
        flags = flags + "||default-view";
      }
      setFlags(flags);
      setLoadedContent(true);
    } else if (checkIfEmailNeedsChange(ts)) {
      // Miro si cliente cumple card de cambiar email (tarda un rato en actualizar la info)
      setFilteredList(changeClientEmail(filteredList, emailList));
    }
  }, [emailList]);

  let ListToRender = [];

  // VERSION PROD
  if (
    !cmsHandler &&
    filteredList &&
    Array.isArray(filteredList) &&
    filteredList.length > 0
  ) {
    ListToRender = filteredList.map((notice, index) => (
      <NoticeBlock
        {...notice}
        key={`notice--${index}`}
        blockName={blockName}
        alias={alias}
        index={index}
        frontalListToDelete={noticeBlockList}
        cmsHandler={cmsHandler}
        loadedContent={loadedContent}
        flags={flags}
      />
    ));
  }

  // VERSION EDITOR
  if (cmsHandler) {
    ListToRender = (
      <>
        <CmsAddElement
          addProps={{
            index: "start",
            idTemplateOrion: ID_TEMPLATE_ORION,
            itemIdDotNotation: `${blockName}.notice-block-list-1`,
            alias,
            layout: "vertical",
          }}
          cmsHandler={cmsHandler}
        />
        {noticeBlockList.map((notice, index) => (
          <NoticeBlock
            {...notice}
            key={`notice--${index}`}
            blockName={blockName}
            alias={alias}
            index={index}
            frontalListToDelete={noticeBlockList}
            cmsHandler={cmsHandler}
            loadedContent={loadedContent}
            flags={flags}
          />
        ))}
        <CmsAddElement
          addProps={{
            index: "end",
            idTemplateOrion: ID_TEMPLATE_ORION,
            itemIdDotNotation: `${blockName}.notice-block-list-1`,
            alias,
            layout: "vertical",
          }}
          cmsHandler={cmsHandler}
        />
      </>
    );
  }

  return (
    <section id={blockName || ""} className="notice-block">
      <div className="container notice-block--content">
        <div className="notice-block--content--title">
          {defaultView ? (
            <TextInfo {...defaultTitle} cmsHandler={cmsHandler} />
          ) : (
            <TextInfo {...title} cmsHandler={cmsHandler} />
          )}
        </div>
        <div className="notice-block--content--list">{ListToRender}</div>
      </div>
      <style jsx>{`.notice-block {
  .notice-block--content {
    background-color: #fff;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    &--list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      width: 57%;
      @media (max-width: 768px) {
        width: 100%;
      }
    }
    &--title {
      width: 57%;
      @media (max-width: 768px) {
        width: 100%;
      }
    }
  }
}
`}</style>
    </section>
  );
};

export default NoticeBlockList;
